<template>
    <div :class="{ 'is-mobile': estMobile }">
        <!-- Popup centrale: affiche l'erreur en cours uniquement -->
        <div v-if="erreurActuelle" class="center-container">
            <div class="popup-erreur" role="dialog" aria-modal="true" aria-labelledby="titre-erreur">
                <button class="close-btn" @click="fermerErreur" aria-label="Fermer">&times;</button>
                <div class="texte-erreur">
                    <h3 id="titre-erreur">{{ erreurActuelle.Titre }}</h3>
                    <p>{{ erreurActuelle.Message }}</p>
                </div>
                <div class="actions">
                    <button class="ok-btn" @click="fermerErreur">OK</button>
                </div>
            </div>
        </div>

        <!-- Bouton flottant "erreur" (bas droite) -->
        <button class="error-fab"
                @click="ouvrirHistorique"
                :aria-expanded="historiqueOuvert ? 'true' : 'false'"
                aria-controls="panneau-erreurs"
                title="Centre des erreurs">
            <!-- Icône triangle alerte -->
            <svg class="error-icon" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M12 2c.37 0 .72.2.9.52l9.06 15.7c.18.32.18.71 0 1.03-.18.32-.52.52-.9.52H2.94c-.38 0-.72-.2-.9-.52a1.06 1.06 0 0 1 0-1.03L11.1 2.52A1.02 1.02 0 0 1 12 2zm0 5c.55 0 1 .45 1 1v6a1 1 0 1 1-2 0V8c0-.55.45-1 1-1zm0 12.25a1.25 1.25 0 1 1 0-2.5 1.25 1.25 0 0 1 0 2.5z" />
            </svg>
            <span class="sr-only">Ouvrir le centre des erreurs</span>
            <!-- Badge non vues -->
            <span v-if="nbNonVues > 0" class="badge">{{ nbNonVues }}</span>
        </button>

        <!-- Panneau historique type “chat” -->
        <div v-if="historiqueOuvert"
             id="panneau-erreurs"
             class="chat-panel"
             role="dialog"
             aria-modal="false"
             aria-label="Centre des erreurs">
            <div class="chat-header">
                <div class="title-wrap">
                    <strong>Centre des erreurs</strong>
                    <small v-if="chargementHistorique" class="muted">chargement…</small>
                </div>
                <div class="header-actions">
                    <button class="mini-btn" @click="rafraichirHistorique" :disabled="chargementHistorique">Rafraîchir</button>
                    <button class="mini-btn danger" @click="toutMarquerCommeVu" :disabled="chargementHistorique || erreursFiltrees.length === 0">
                        Tout marquer comme vu
                    </button>
                    <button class="mini-btn ghost" @click="fermerHistorique" aria-label="Fermer le panneau">✕</button>
                </div>
            </div>

            <!-- Filtres -->
            <div class="filters">
                <button class="filter-btn"
                        :class="{ active: filtreHistorique === 'toutes' }"
                        @click="filtreHistorique = 'toutes'">
                    Toutes
                </button>
                <button class="filter-btn"
                        :class="{ active: filtreHistorique === 'nonvues' }"
                        @click="filtreHistorique = 'nonvues'">
                    Non vues
                </button>
                <button class="filter-btn"
                        :class="{ active: filtreHistorique === 'vues' }"
                        @click="filtreHistorique = 'vues'">
                    Vues
                </button>
            </div>

            <!-- Liste -->
            <div class="chat-content">
                <template v-if="erreursFiltrees.length > 0">
                    <div class="err-item"
                         v-for="(e, idx) in erreursFiltrees"
                         :key="e.IdErreur || idx"
                         :class="{ seen: e.HasBeenSeen }">
                        <div class="err-header">
                            <span class="dot" :class="{ unseen: !e.HasBeenSeen }" aria-hidden="true"></span>
                            <span class="err-title">{{ e.Titre || '(Sans titre)' }}</span>
                        </div>
                        <div class="err-message">{{ e.Message }}</div>

                        <div class="err-meta">
                            <span class="status" :class="{ unseen: !e.HasBeenSeen }">
                                {{ e.HasBeenSeen ? 'Vu' : 'Non vu' }}
                            </span>

                            <div class="err-actions">
                                <button class="mini-btn" @click="marquerCommeVu(e)" :disabled="e.HasBeenSeen">
                                    Marquer comme vu
                                </button>
                                <button class="mini-btn danger" @click="supprimerErreur(e)">
                                    Supprimer
                                </button>
                            </div>
                        </div>
                    </div>
                </template>

                <div v-else class="empty-state">
                    Aucune erreur à afficher pour ce filtre.
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import { mapMutations } from 'vuex';
    import ErreurAffichable from '@/script/TransCam/ErreurAffichable';
    import RequestManager from '@/script/request';

    export default {
        name: 'AffichageErreurs',

        data() {
            return {
                // Popup séquentielle (non vues)
                erreurs: [],
                erreurActuelle: null,
                enCoursDAffichage: false,
                intervalErreur: null,

                // Historique type chat
                historiqueOuvert: false,
                erreursHistorique: [],
                filtreHistorique: 'toutes', // 'toutes' | 'nonvues' | 'vues'
                chargementHistorique: false,

                // Badge non vues (mis à jour via le polling existant)
                nbNonVues: 0,
            };
        },

        computed: {
            // Le WebView MAUI mobile n'honore pas width=device-width (largeur CSS >= 900px),
            // donc les media queries de largeur ne se declenchent pas. On pilote le layout
            // mobile (bottom-sheet) via le flag estMobile de l'app (pose par l'hote MAUI),
            // fiable quel que soit la largeur CSS rapportee par le WebView.
            estMobile() {
                return RequestManager.estMobile;
            },
            erreursFiltrees() {
                if (!Array.isArray(this.erreursHistorique)) return [];
                if (this.filtreHistorique === 'nonvues') {
                    return this.erreursHistorique.filter(e => !e.HasBeenSeen);
                }
                if (this.filtreHistorique === 'vues') {
                    return this.erreursHistorique.filter(e => e.HasBeenSeen);
                }
                return this.erreursHistorique;
            },
        },

        methods: {
            ...mapMutations('ErreurTransCAM', ['setErreur']),

            // --- Polling des erreurs non vues (pour la popup et le badge)
            CheckerErreurTransCAM() {
                if (this.intervalErreur) return; // éviter plusieurs intervals

                this.intervalErreur = setInterval(() => {
                    if (this.enCoursDAffichage) return; // on n'empile pas pendant un affichage

                    this.enCoursDAffichage = true; // lock le temps de la requête

                    ErreurAffichable.getAllErreurs()
                        .then((data) => {
                            // Filtrer non vues pour la file d'attente popup
                            this.erreurs = [];
                            for (let i = 0; i < data.length; i++) {
                                if (data[i].HasBeenSeen === false) {
                                    this.erreurs.push(data[i]);
                                }
                            }

                            // Mettre à jour badge
                            this.nbNonVues = this.erreurs.length;

                            // Mettre à jour le store si besoin
                            this.setErreur(this.erreurs);

                            if (this.erreurs.length > 0) {
                                this.afficherErreursUneParUne();
                            } else {
                                // Rien à afficher -> on libère le flag
                                this.enCoursDAffichage = false;
                            }
                        })
                        .catch((error) => {
                            console.error('Erreur lors de la récupération des erreurs :', error);
                            this.enCoursDAffichage = false;
                        });
                }, 3000);
            },

            // --- Affiche la première erreur de la file (pas de fermeture auto)
            afficherErreursUneParUne() {
                if (this.erreurs.length === 0) {
                    this.erreurActuelle = null;
                    this.enCoursDAffichage = false;
                    return;
                }
                const erreur = this.erreurs.shift();
                this.erreurActuelle = erreur;
            },

            // --- Ferme la popup et marque l'erreur comme vue, puis passe à la suivante
            async fermerErreur() {
                const current = this.erreurActuelle;

                // Cache immédiatement la popup
                this.erreurActuelle = null;

                // Marque comme vue côté front
                if (current) current.HasBeenSeen = true;

                // Mise à jour backend
                try {
                    if (current && current.IdErreur) {
                        await ErreurAffichable.consulterErreur(current.IdErreur);
                    }
                } catch (err) {
                    console.error('Erreur marquage comme vue :', err);
                }

                // Mettre à jour badge immédiatement
                this.nbNonVues = Math.max(0, this.nbNonVues - 1);

                // Si le panneau historique est ouvert, rafraîchir
                if (this.historiqueOuvert) {
                    this.rafraichirHistoriqueSilencieux();
                }

                // Enchaîne s'il reste des erreurs
                if (this.erreurs.length > 0) {
                    this.afficherErreursUneParUne();
                } else {
                    this.enCoursDAffichage = false;
                }
            },

            // --- UI historique
            // IMPORTANT (mobile/WebView) : le FAB OUVRE seulement (idempotent), il ne bascule
            // pas. Sur tactile, un tap genere souvent touchend PUIS un "ghost click" ~300ms
            // plus tard : avec un toggle, ca ouvrait puis refermait aussitot (le panneau
            // "s'affichait tres vite puis disparaissait"). En ouverture pure, le 2e evenement
            // est sans effet. La fermeture se fait via le bouton ✕ du panneau.
            async ouvrirHistorique() {
                if (this.historiqueOuvert) return; // deja ouvert -> ghost click sans effet
                this.historiqueOuvert = true;
                await this.rafraichirHistorique();
            },
            fermerHistorique() {
                this.historiqueOuvert = false;
            },
            // Conserve pour compat (non utilise par le FAB/✕ desormais).
            async toggleHistorique() {
                if (this.historiqueOuvert) { this.fermerHistorique(); }
                else { await this.ouvrirHistorique(); }
            },

            async rafraichirHistorique() {
                try {
                    this.chargementHistorique = true;
                    const data = await ErreurAffichable.getAllErreurs(); // renvoie tout (vues et non vues)
                    this.erreursHistorique = Array.isArray(data) ? data.slice() : [];
                } catch (e) {
                    console.error('rafraichirHistorique -->', e);
                } finally {
                    this.chargementHistorique = false;
                }
            },

            async rafraichirHistoriqueSilencieux() {
                try {
                    const data = await ErreurAffichable.getAllErreurs();
                    this.erreursHistorique = Array.isArray(data) ? data.slice() : [];
                } catch (e) {
                    // silencieux
                }
            },

            async marquerCommeVu(e) {
                if (!e || e.HasBeenSeen) return;
                e.HasBeenSeen = true; // optimiste

                try {
                    if (e.IdErreur) {
                        await ErreurAffichable.consulterErreur(e.IdErreur);
                    }
                } catch (err) {
                    console.error('marquerCommeVu -->', err);
                    e.HasBeenSeen = false; // rollback si échec
                    return;
                }

                // mettre à jour badge si c’était une non vue
                this.nbNonVues = Math.max(0, this.nbNonVues - 1);
            },

            async toutMarquerCommeVu() {
                const aMarquer = this.erreursHistorique.filter(x => !x.HasBeenSeen);
                if (aMarquer.length === 0) return;

                // Optimiste côté front
                aMarquer.forEach(x => (x.HasBeenSeen = true));
                const anciennesNonVues = this.nbNonVues;
                this.nbNonVues = 0;

                try {
                    await Promise.all(
                        aMarquer
                            .filter(x => x && x.IdErreur)
                            .map(x => ErreurAffichable.consulterErreur(x.IdErreur))
                    );
                } catch (err) {
                    console.error('toutMarquerCommeVu -->', err);
                    // rollback simple si nécessaire
                    this.nbNonVues = anciennesNonVues;
                    this.rafraichirHistoriqueSilencieux();
                }
            },

            async supprimerErreur(e) {
                if (!e || !e.IdErreur) return;
                const id = e.IdErreur;
                // Optimiste: on retire localement
                this.erreursHistorique = this.erreursHistorique.filter(x => x.IdErreur !== id);
                // Ajuste le badge si c’était une non vue
                if (!e.HasBeenSeen) {
                    this.nbNonVues = Math.max(0, this.nbNonVues - 1);
                }
                try {
                    await ErreurAffichable.supprimerErreur(id);
                } catch (err) {
                    console.error('supprimerErreur -->', err);
                    // En cas d'échec, on resynchronise
                    this.rafraichirHistoriqueSilencieux();
                }
            },
        },

        mounted() {
            this.CheckerErreurTransCAM();
        },

        beforeUnmount() {
            clearInterval(this.intervalErreur);
        },
    };
</script>

<style scoped>
    /* ===== Popup centrale ===== */
    .center-container {
        position: fixed;
        top: 100px;
        left: 50%;
        transform: translateX(-50%);
        z-index: 2147483000;
        pointer-events: none;
    }

    .popup-erreur {
        position: relative;
        background: #f44336;
        color: white;
        padding: 24px 32px;
        border-radius: 12px;
        box-shadow: 0 0 20px rgba(0, 0, 0, 0.4);
        max-width: 500px;
        width: 100%;
        text-align: center;
        pointer-events: all;
    }

    .close-btn {
        position: absolute;
        top: 8px;
        right: 12px;
        background: transparent;
        border: none;
        color: white;
        font-size: 24px;
        font-weight: bold;
        cursor: pointer;
        padding: 0;
        line-height: 1;
    }

    .texte-erreur {
        padding-right: 24px;
        margin-bottom: 16px;
    }

    .actions {
        display: flex;
        justify-content: center;
    }

    .ok-btn {
        background: #ffffff;
        color: #f44336;
        border: none;
        border-radius: 8px;
        padding: 10px 18px;
        font-weight: 700;
        cursor: pointer;
    }

        .ok-btn:hover {
            opacity: 0.9;
        }

    /* ===== FAB Erreur & Panneau ===== */
    .error-fab {
        position: fixed;
        bottom: 20px;
        right: 20px;
        height: 56px;
        min-width: 56px;
        padding: 0 16px;
        border-radius: 9999px;
        border: none;
        background: #ef4444; /* rouge alerte */
        color: #fff;
        box-shadow: 0 10px 25px rgba(0,0,0,.25);
        display: inline-flex;
        align-items: center;
        gap: 8px;
        cursor: pointer;
        /* Au-dessus de tout (certains elements mobiles sont aussi a 10000, ex. Fse.vue
           .affiche-mobile-divers, et a z-index egal ils captaient le tap du FAB). */
        z-index: 2147483001;
    }

        .error-fab:hover {
            filter: brightness(1.06);
        }

    .error-icon {
        width: 22px;
        height: 22px;
        display: block;
        fill: currentColor;
    }

    .badge {
        background: #ffffff; /* contraste sur bouton rouge */
        color: #111827;
        font-size: 12px;
        line-height: 1;
        padding: 4px 8px;
        border-radius: 9999px;
        font-weight: 700;
    }

    .chat-panel {
        position: fixed;
        bottom: 90px;
        right: 20px;
        width: 360px;
        max-height: 70vh;
        background: #fff;
        color: #111827;
        border-radius: 12px;
        box-shadow: 0 18px 40px rgba(0,0,0,.28);
        display: flex;
        flex-direction: column;
        overflow: hidden;
        z-index: 2147483001;
    }

    .chat-header {
        padding: 10px 12px;
        background: #111827;
        color: #fff;
        display: flex;
        align-items: center;
        gap: 8px;
        justify-content: space-between;
    }

    .title-wrap {
        display: flex;
        flex-direction: column;
    }

    .muted {
        opacity: 0.8;
        font-size: 12px;
    }

    .header-actions {
        display: flex;
        gap: 6px;
    }

    .mini-btn {
        background: #e5e7eb;
        color: #111827;
        border: none;
        border-radius: 8px;
        padding: 6px 10px;
        font-size: 12px;
        cursor: pointer;
    }

        .mini-btn:hover {
            filter: brightness(0.97);
        }

        .mini-btn.danger {
            background: #fee2e2;
            color: #b91c1c;
        }

        .mini-btn.ghost {
            background: transparent;
            color: #fff;
            border: 1px solid rgba(255,255,255,.25);
        }

    .filters {
        display: flex;
        gap: 6px;
        padding: 8px;
        border-bottom: 1px solid #e5e7eb;
    }

    .filter-btn {
        background: #f3f4f6;
        border: 1px solid transparent;
        color: #111827;
        padding: 6px 10px;
        border-radius: 9999px;
        font-size: 12px;
        cursor: pointer;
    }

        .filter-btn.active {
            background: #111827;
            color: #fff;
        }

    .chat-content {
        padding: 8px;
        overflow-y: auto;
        max-height: calc(70vh - 110px);
    }

    .err-item {
        border: 1px solid #e5e7eb;
        border-radius: 10px;
        padding: 10px;
        margin-bottom: 8px;
        background: #fff;
    }

        .err-item.seen {
            opacity: 0.8;
        }

    .err-header {
        display: flex;
        align-items: center;
        gap: 8px;
        margin-bottom: 6px;
    }

    .err-title {
        font-weight: 700;
        font-size: 14px;
    }

    .dot {
        width: 10px;
        height: 10px;
        border-radius: 9999px;
        background: #9ca3af;
        display: inline-block;
    }

        .dot.unseen {
            background: #ef4444;
        }

    .err-message {
        font-size: 13px;
        color: #111827;
        margin-bottom: 8px;
        white-space: pre-wrap;
    }

    .err-meta {
        display: flex;
        align-items: center;
        justify-content: space-between;
    }

    .status {
        font-size: 12px;
        padding: 2px 8px;
        border-radius: 9999px;
        background: #f3f4f6;
        color: #111827;
    }

        .status.unseen {
            background: #fee2e2;
            color: #b91c1c;
        }

    /* ===== Mobile via flag app (estMobile) — fiable en WebView MAUI ===== */
    /* Le WebView MAUI mobile rend a une largeur CSS >= 900px (ignore device-width), donc
       les media queries de largeur ne marchent pas la-bas. On force le bottom-sheet quand
       l'app est en mode mobile, quelle que soit la largeur rapportee. */
    .is-mobile .chat-panel {
        left: 0;
        right: 0;
        width: auto;
        /* IMPORTANT : NE PAS utiliser vh ici. Le WebView MAUI mobile casse les unites vh
           (height:75vh donnait 0 -> panneau invisible). On derive la hauteur du positionnement
           top+bottom (relatif au viewport, en px), qui lui fonctionne. Sheet quasi plein ecran
           laissant un petit apercu de la page en haut. */
        top: 64px;
        bottom: 0;
        height: auto;
        max-height: none;
        min-height: 0;
        border-radius: 14px 14px 0 0;
        padding-bottom: env(safe-area-inset-bottom, 0px);
    }

    /* La liste remplit tout l'espace restant du sheet et scrolle (min-height:0
       indispensable pour que le scroll fonctionne dans un conteneur flex). */
    .is-mobile .chat-content {
        flex: 1 1 auto;
        min-height: 0;
        max-height: none;
    }

    .is-mobile .error-fab {
        right: 16px;
        bottom: calc(88px + env(safe-area-inset-bottom, 0px));
    }

    /* ===== Mobile / WebView ===== */
    /* IMPORTANT : sur telephone (Android/iOS en WebView), le bas de l'ecran est la zone
       de gestes / barre de navigation systeme. Un bouton colle au bord (bottom: 20px)
       y est VISIBLE mais ses taps sont captes par l'OS AVANT la WebView -> le FAB ne
       repond pas (alors qu'il marche en navigateur desktop, sans cette zone). On REMONTE
       donc le FAB nettement au-dessus du bord, en respectant safe-area-inset-bottom.
       Le panneau passe aussi en pleine largeur (sinon il debordait en 360px fixe). */
    @media (max-width: 900px) {
        /* Bottom-sheet : ancre aux bords bas/gauche/droite. Robuste contre le hors-cadre
           (panneau qui sortait a droite) et contre un eventuel overflow horizontal de page. */
        .chat-panel {
            left: 0;
            right: 0;
            width: auto;
            bottom: 0;
            top: auto;
            max-height: 78vh;
            border-radius: 14px 14px 0 0;
            padding-bottom: env(safe-area-inset-bottom, 0px);
        }

        .chat-content {
            max-height: calc(78vh - 130px);
        }

        /* FAB remonte au-dessus de la zone de gestes/navigation systeme (sinon l'OS
           capte le tap avant la WebView et le bouton ne repond pas). */
        .error-fab {
            right: 16px;
            bottom: calc(88px + env(safe-area-inset-bottom, 0px));
        }
    }

    /* Accessibilité helper (visually hidden) */
    .sr-only {
        position: absolute !important;
        width: 1px;
        height: 1px;
        padding: 0;
        margin: -1px;
        overflow: hidden;
        clip: rect(0,0,0,0);
        white-space: nowrap;
        border: 0;
    }
</style>
